import React, { useState, useEffect } from 'react';
import { X, ShoppingCart, Receipt, AlertTriangle, ExternalLink, Briefcase, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLumisial } from '../context/LumisialContext';
import { actividadService } from '../services/actividadService';
import { ActividadEconomica } from '../lib/supabase';
import { Select } from './ui/Select';
import { DialogShell } from './ui/DialogShell';

interface SelectorActividadRapidoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPedido: (actividadId: string) => void;
  onSelectGasto: (actividadId: string) => void;
}

export const SelectorActividadRapidoModal: React.FC<SelectorActividadRapidoModalProps> = ({
  isOpen,
  onClose,
  onSelectPedido,
  onSelectGasto
}) => {
  const { selectedLumisial } = useLumisial();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actividades, setActividades] = useState<ActividadEconomica[]>([]);
  const [actividadId, setActividadId] = useState('');

  useEffect(() => {
    if (isOpen && selectedLumisial) {
      loadActividades();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedLumisial]);

  const loadActividades = async () => {
    if (!selectedLumisial) return;

    try {
      setLoading(true);
      const activas = await actividadService.getActiveByLumisial(selectedLumisial.id);
      setActividades(activas);

      if (activas.length > 0) {
        const ultima = localStorage.getItem('ultima_actividad_usada');
        if (ultima && activas.find(a => a.id === ultima)) {
          setActividadId(ultima);
        } else {
          setActividadId(activas[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading actividades:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (tipo: 'pedido' | 'gasto') => {
    if (!actividadId) return;
    localStorage.setItem('ultima_actividad_usada', actividadId);
    onClose();
    if (tipo === 'pedido') {
      onSelectPedido(actividadId);
    } else {
      onSelectGasto(actividadId);
    }
  };

  const handleIrAActividades = () => {
    onClose();
    navigate('/actividades');
  };

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-10">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
          <p className="text-slate-500 text-sm">Cargando actividades...</p>
        </div>
      );
    }
    if (actividades.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="bg-amber-100 p-3 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-1">No hay actividades activas</h3>
          <p className="text-slate-600 text-sm mb-5">
            Crea una actividad para registrar pedidos y gastos
          </p>
          <button
            onClick={handleIrAActividades}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Ir a Actividades
          </button>
        </div>
      );
    }
    return (
      <div className="space-y-5">
        <div>
          <Select
            label={`Actividad${actividades.length === 1 ? ' (Única actividad activa)' : ''}`}
            options={actividades.map((a: ActividadEconomica) => ({
              value: a.id,
              label: `${a.nombre} - ${a.estado === 'planificacion' ? 'Planificacion' : 'En Curso'}`,
            }))}
            value={actividadId}
            onChange={setActividadId}
            disabled={actividades.length === 1}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleSelect('pedido')}
            className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition-all duration-200 group"
          >
            <div className="bg-emerald-600 p-3 rounded-xl group-hover:scale-110 transition-transform duration-200">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-emerald-800 text-sm">Registrar Pedido</span>
          </button>

          <button
            onClick={() => handleSelect('gasto')}
            className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-400 transition-all duration-200 group"
          >
            <div className="bg-orange-600 p-3 rounded-xl group-hover:scale-110 transition-transform duration-200">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-orange-800 text-sm">Registrar Gasto</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      header={(
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Briefcase className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-bold">Actividad Economica</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    >
      {renderContent()}
    </DialogShell>
  );
};
